import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import type { TFields, TGenderField } from '../../models/analytics';

import type { IAnalyticsSender, TMappedFields } from './models';
import { isValidFioFields, onBlurFieldAnalytics } from './helpers';

function useCachedValue<T>(initValue: T): [T, (newValue: T) => void] {
    const value = useRef<T>(initValue);

    return [
        value.current,
        newValue => {
            value.current = newValue;
        },
    ];
}

export const Sender: FC<IAnalyticsSender> = ({ active, analytics, fields }) => {
    /**
     * кэширование значений для последующих сравнений (prevState vs currentState)
     * */
    const [cachedFields, setCachedFields] = useCachedValue<TMappedFields>(fields);
    const [cachedActive, setCachedActive] = useCachedValue<TFields>(active);
    const genderRefValue = useRef<string>('');

    /**
     * аналитика на "фокус"
     * */
    useEffect(() => {
        /**
         * при переключении между полями `active` становится `undefined`
         * отправка только при `active` соответствующем полю формы и наличии аналитики на это поле
         * */
        if (active && analytics[active]) {
            sendEvent(analytics[active]?.focus);
            /**
             * запоминаем значение `active`, для использования в аналитике на "блюр"
             * */
            setCachedActive(active);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    /**
     * аналитика на "блюр"
     * слушает изменения объекта `fields`, который меняется только при переключении фокуса
     * */
    useEffect(() => {
        const activeField = fields[cachedActive];
        const cachedActiveField = cachedFields[cachedActive];

        /**
         * при первом рендере формы объект с полями пустой, поэтому проверяем наличие поля
         * */
        if (activeField) {
            onBlurFieldAnalytics({
                active,
                activeField,
                cachedActiveField,
                analytics,
                cachedActive,
                fields,
            });
            /**
             * обновление кэша для следующих проверок
             * */
            setCachedFields({ ...cachedFields, [cachedActive]: activeField });
        }

        /**
         * Отправляет эвент об успешном заполнении, когда заполнено ФИО в 3 поля
         * */
        if (!fields.fullName && isValidFioFields(fields)) {
            sendEvent(analytics.fullName?.correct);
        }
    }, [fields, cachedActive, cachedFields, active]);

    /**
     * аналитика на переключение gender-button
     * */
    useEffect(() => {
        const sendGenderAnalytic = (fieldName: TGenderField) => {
            const gender = fields[fieldName];
            if (!gender) return;

            const { touched, value } = gender;

            if (value && genderRefValue.current !== value) {
                if (touched) {
                    sendEvent({
                        ...(analytics?.sexChoice || {}),
                        label_detail: value === 'f' ? 'Женский' : 'Мужской',
                    });
                }
                genderRefValue.current = value;
            }
        };

        sendGenderAnalytic('sex');
        sendGenderAnalytic('gender');
    }, [fields.sex, fields.gender, analytics]);

    return null;
};
